import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Button, message, Modal, Radio, Space, Spin } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import s from './s.module.less';
import { PATH } from 'constants/path';
import StepIndicator from './components/StepIndicator';
import BasicInfoStep from './components/BasicInfoStep';
import LicenseStep from './components/LicenseStep';
import InsuranceStep from './components/InsuranceStep';
import ProfessionalProfileStep from './components/ProfessionalProfileStep';
import BackgroundStep from './components/BackgroundStep';
import ReviewStep from './components/ReviewStep';
import { submitPreview } from 'api/applicationForm';
import useLoadBasicData from 'hooks/useLoadBasicData';
import { EKey, getUserId, getAvaDocumentId } from 'utils/localstore';
import { PRIVACY_LINK, TERMS_LINK, CHANNEL_DETAIL } from 'constants/common';
import { getUrlParam } from 'utils/common';
import useGetUniprofileResumeInfo from 'hooks/useGetUniprofileResumeInfo';

const LOCAL_STORAGE_KEY_PREFIX = EKey.UNI_PROFILE_STEP;
const getLocalStorageKey = () => {
    const userId = getUserId();
    return `${LOCAL_STORAGE_KEY_PREFIX}_${userId}`;
};

const NewUniProfile: React.FC = () => {
    const navigate = useNavigate();
    const isAva = getUrlParam('isAva') === 'true';
    const from = getUrlParam('from');
    const [_, infoLoading, _2, fetchFn] = useGetUniprofileResumeInfo();
    const [params] = useSearchParams();
    const channelId = Number(params.getAll('channelId')[0]);

    const [loading, setLoading] = useState<boolean>(false);
    const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
    const [allowAuth, setAllowAuth] = useState<boolean>(true);
    const [showUseAvaTip, setShowUseAvaTip] = useState<boolean>(true);
    const [forceCloseUseAvaTip, setForceCloseUseAvaTip] = useState(false);
    const ref = useRef<any>();

    const steps = [
        { id: 0, title: 'Basic information', component: <BasicInfoStep ref={ref} /> },
        { id: 1, title: 'License', component: <LicenseStep ref={ref} /> },
        { id: 2, title: 'Insurance', component: <InsuranceStep ref={ref} /> },
        { id: 3, title: 'Professional profile', component: <ProfessionalProfileStep ref={ref} /> },
        { id: 4, title: 'Background (optional)', component: <BackgroundStep ref={ref} /> },
        { id: 5, title: 'Review', component: <ReviewStep /> },
    ];

    const [currentStep, setCurrentStep] = useState<number>(() => {
        const savedStep = localStorage.getItem(getLocalStorageKey());
        if (savedStep) {
            return parseInt(savedStep, 10);
        }
        return 0;
    });

    const stepTitles = steps.map((step) => ({ id: step.id, title: step.title }));

    const [loadBasicData] = useLoadBasicData();

    useEffect(() => {
        loadBasicData();
    }, [loadBasicData]);

    useEffect(() => {
        localStorage.setItem(getLocalStorageKey(), currentStep.toString());
    }, [currentStep]);

    useEffect(() => {
        const documentId = getAvaDocumentId();
        if (isAva && documentId) {
            fetchFn(documentId);
        }
    }, [isAva]);

    useEffect(() => {
        if (forceCloseUseAvaTip) {
            setShowUseAvaTip(false);
        } else {
            setShowUseAvaTip(!isAva && currentStep !== steps.length - 1);
        }
    }, [isAva, currentStep]);

    const scrollToTop = useCallback(() => {
        document.querySelector(`.${s.content}`)?.scrollTo(0, 0);
    }, []);

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const res = await submitPreview({
                allowAuth,
                channelId: 1,
            });

            if (res && !res.error) {
                if (from === CHANNEL_DETAIL) {
                    navigate(`${PATH.DASHBOARD}/${PATH.CHANNEL_DETAIL}?channelId=${channelId}`, { replace: true });
                } else {
                    navigate('/channel-success');
                }
            } else {
                message.error(res.error);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const goToNextStep = useCallback(async () => {
        try {
            setLoading(true);
            if (ref.current?.onSubmit) {
                await ref.current.onSubmit();
            }
            const documentId = getAvaDocumentId();
            if (isAva && documentId) {
                await fetchFn(documentId);
            }
            if (currentStep < steps.length - 1) {
                setCurrentStep(currentStep + 1);
                scrollToTop();
            } else {
                setShowAuthModal(true);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [currentStep, scrollToTop, navigate]);

    const goToPreviousStep = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            scrollToTop();
        }
    }, [currentStep, scrollToTop]);

    // const handleStepClick = useCallback((step: number) => {
    //     setCurrentStep(step);
    //     scrollToTop();
    // }, [scrollToTop]);

    const renderStepContent = steps[currentStep].component;

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <img
                    onClick={() => {
                        navigate(PATH.DASHBOARD);
                    }}
                    src="https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/ui/logo_kiwihealth_by_klarity_380x96.png"
                    className={s.logo}
                    alt="Kiwi Health Logo"
                />
            </div>

            <div className={s.content}>
                <div className={s.stepIndicatorContainer}>
                    <h2 className={s.title}>UniProfile</h2>
                    <StepIndicator
                        steps={stepTitles}
                        currentStep={currentStep}
                    />
                </div>

                <div className={s.stepContent}>
                    {
                        showUseAvaTip && (
                            <div className={s.useAvaTip} id="ava-tips">
                                <div className={s.useAvaTipInner}>
                                    <div className={s.tipText}>💡 Tired of manual entry? Upload your resume & let <span className={s.avaLogo}>Ava</span> do the work!</div>
                                    <div className={s.actionBox}>
                                        <Button
                                            className={s.backToUploadButton}
                                            onClick={() => {
                                                navigate(`/${PATH.UNIPROFILE_RESUME}?type=uniprofile`);
                                            }}
                                        >Upload my resume
                                        </Button>
                                        <div
                                            className={s.closeIcon}
                                            onClick={() => {
                                                setShowUseAvaTip(false);
                                                setForceCloseUseAvaTip(true);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )
                    }
                    <div className={s.requiredFields}>Indicates required fields</div>
                    {infoLoading ? <Spin /> : renderStepContent}
                </div>
            </div>

            <div className={s.footer}>
                <div className={s.btns}>
                    <Button
                        onClick={() => {
                            navigate(PATH.DASHBOARD);
                        }}
                    >
                        Exit
                    </Button>
                    {currentStep > 0 && (
                        <Button
                            type="text"
                            onClick={goToPreviousStep}
                            className={s.prevButton}
                        >
                            Back
                        </Button>
                    )}
                </div>
                <Button type="primary" className={s.exitButton} onClick={goToNextStep} loading={loading}>
                    {currentStep === steps.length - 1 ? 'Submit for review' : 'Next'}
                </Button>
            </div>

            <Modal
                title="Uniprofile"
                centered
                open={showAuthModal}
                okText="Confirm"
                onOk={handleSubmit}
                onCancel={() => { setShowAuthModal(false); }}
                width={543}
            >
                <div className={s.modalContent}>
                    <p className={s.modalContentText}>While reviewing your application, you may have missing/incorrect information. Does Kiwi Health's Provider Support team have the permission to edit/improve your profile? You can always edit your preferences later.</p>
                    <Radio.Group onChange={(e) => setAllowAuth(e.target.value)} value={allowAuth}>
                        <Space direction="vertical">
                            <Radio value>Yes, I authorize the Provider Support team to edit my profile</Radio>
                            <Radio value={false}>No, I prefer to edit my profile myself</Radio>
                        </Space>
                    </Radio.Group>
                    <p className={s.modalContentExp}>By clicking on the “Confirm” button, you agree to the Kiwi Health
                        <a style={{ color: '#4849ff' }} target="_blank" href={TERMS_LINK} rel="noreferrer"> terms </a>
                        and
                        <a style={{ color: '#4849ff' }} target="_blank" href={PRIVACY_LINK} rel="noreferrer"> privacy policy. </a>
                    </p>
                </div>
            </Modal>
        </div>
    );
};

export default NewUniProfile;
